'use client';

import {
  FC,
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { initializeClevertap } from '@/utils/clevertap';
import { syncAndGetUtmParams } from '@/utils/utmTracking';
import CleverTap from 'clevertap-web-sdk/clevertap';
import { usePathname } from 'next/navigation';
import { getKeyValue } from '@/utils/helper';
import { ProductItemPropTypes } from '@/components/Card/Product';
import { logger, storage } from '@/config';
import { DEFAULT_STORE_CODE, TOKEN_EXPIRY_TIME } from '@/constants';
import { getCookie, setCookie } from 'cookies-next';
import { getABTestingUserGroup } from '@/services';

interface PropTypes {
  children: React.ReactNode;
}

interface useCleverTapPropTypes {
  clevertapUserLogin: (user: any) => void;
  pushEvent: ({ event, data }: EventsType) => void;
  updateUserCartClevertap: ({
    cartItems,
  }: {
    cartItems: ProductItemPropTypes[] | null;
  }) => void;
  updateClevertapUserAttributes: ({
    attributesToUpdate,
  }: {
    attributesToUpdate: { key: string; value: string }[];
  }) => void;
}

type EventsType = {
  event:
    | 'Sign Up'
    | 'Login'
    | 'Searched'
    | 'Search Result Clicked'
    | 'search_zero_results'
    | 'content_card_clicked'
    | 'Add To Cart Clicked'
    | 'Search Result Add To Cart'
    | 'Search Result Wishlist'
    | 'Category Viewed'
    | 'Product Viewed'
    | 'Recipe Viewed'
    | 'Page Viewed'
    | 'Payment Failed'
    | 'Registered for Newsletter'
    | 'Added Product To Cart'
    | 'Updated Product In Cart'
    | 'Removed Product From Cart'
    | 'Checkout Started'
    | 'Payment Initiated'
    | 'Filter Applied'
    | 'Filter Removed'
    | 'Sort Applied'
    | 'Website Landing'
    | 'Wishlist updated'
    | 'Banner Clicked'
    | 'Home Page Category Clicked'
    | 'Additional Link Clicked'
    | 'Shop By Lifestyle Clicked'
    | 'Product Carousel Clicked'
    | 'Article Clicked'
    | 'Navigation Link Clicked'
    | 'Hover Category Clicked'
    | 'Cart Error'
    | 'Whatsapp Interaction'
    | 'GPS Location Fetched'
    | 'GPS Native Prompt Shown'
    | 'Website Location Prompt Shown'
    | 'Website Location Prompt Clicked'
    | 'Location Searched'
    | 'Geolocation request failed'
    | 'GPS Location Permission Query'
    | 'Searchbar Clicked'
    | 'Cart Opened'
    | 'Login Flow'
    | 'Stopped Login Flow'
    | 'Checkout Address Step'
    | 'Checkout Slot Selection Step'
    | 'Delivery Details Dialog Shown'
    | 'Delivery Details Dialog Clicked'
    | 'Delivery Details Dialog Continue'
    | 'Suggested Location Selected'
    | 'Payment Step Clicked'
    | 'Popular Search Clicked'
    | 'Cart Viewed'
    | 'Checkout Started'
    | 'Delivery Slot Selected'
    | 'Payment Started'
    | 'Payment Failed'
    | 'first_purchase'
    | 'repeat_purchase'
    | 'nth_purchase'
    | 'user_type_identified'
    | 'Cart - Progress Bar'
    | 'Form Submitted';
  data: any;
};

const Context = createContext<useCleverTapPropTypes | null>(null);

const CleverTapProvider: FC<PropTypes> = ({ children }) => {
  const pathname = usePathname();
  const [clevertapModule, setClevertapModule] = useState<CleverTap | null>(
    null
  );

  const clevertapInit = useCallback(async () => {
    let clevertap = clevertapModule;
    if (!clevertap) {
      clevertap = await initializeClevertap();
      setClevertapModule(clevertap);
    }
    const landingEvent = sessionStorage.getItem('landingEvent');

    const urlParams = new URLSearchParams(window.location.search);
    const utmParams = syncAndGetUtmParams();
    const utmObject = {
      utm_source: urlParams.get('utm_source'),
      utm_medium: urlParams.get('utm_medium'),
      utm_campaign: urlParams.get('utm_campaign'),
      referrer_source: document.referrer || 'Direct',
    };

    const storedUtmObject = sessionStorage.getItem('utmObject');
    if (!storedUtmObject) {
      sessionStorage.setItem('utmObject', JSON.stringify(utmObject));
    }
    if (!landingEvent) {
      const paths = pathname.split('/');
      const category =
        paths[paths.length - 2] === '' ? 'home' : paths[paths.length - 2];
      const name =
        window.document.title !== '' ? window.document.title : category;
      const abGroup = await getABTestingUserGroup();
      const abTestingUserId = await getCookie('user-id');

      let geoData: any = null;
      try {
        const response = await fetch('/api/location');
        if (response.ok) {
          geoData = await response.json();
          // console.log('response', geoData);
          storage.set('geoData', JSON.stringify(geoData));
          if (geoData?.country || geoData?.city) {
            clevertap?.profile.push({
              Site: {
                Country: geoData.country,
                State: geoData.region,
                City: geoData.city,
                Latitude: geoData.latitude,
                Longitude: geoData.longitude,
              },
            });
          }

          clevertap?.getLocation(
            Number(geoData.latitude),
            Number(geoData.longitude)
          );
        }
      } catch (error) {
        logger.error('Failed to fetch geolocation data:', error);
      }

      clevertap?.event.push('Website Landing', {
        'Page URL': `${window.location.origin}${pathname}`,
        'Page Name': name,
        'Page Category': category,
        Timestamp: new Date().getTime(),
        'Referral Source': document.referrer || 'Direct',
        Browser: navigator.userAgent,
        'Operating System': navigator.platform,
        utm_source: utmParams.utm_source,
        utm_medium: utmParams.utm_medium,
        utm_campaign: utmParams.utm_campaign,
        Serviceable: storage.get('serviceable'),
        'Store Code': storage.get('store_code') || 'Not Set',
        'AB Testing User ID': abTestingUserId,
        'AB Testing': abGroup
          ?.map(
            (group: { group_name: any; experiment_name: any }) =>
              `${group.group_name}:${group.experiment_name}`
          )
          .join(';'),
        ...(geoData?.country && { Country: geoData.country }),
        ...(geoData?.city && { City: geoData.city }),
        ...(geoData?.region && { State: geoData.region }),
      });
      sessionStorage.setItem('landingEvent', 'true');
    }
  }, [clevertapModule, pathname]);

  const saveCtIdentityInStorage = useCallback(async (id: string) => {
    storage.set('ctIdentity', id);
    setCookie('ctIdentity', id, {
      maxAge: TOKEN_EXPIRY_TIME,
    });
  }, []);

  useEffect(() => {
    if (clevertapModule) {
      const ctIdentity = window.clevertap.getCleverTapID();
      if (ctIdentity) {
        saveCtIdentityInStorage(ctIdentity);
      }
    }
  }, [clevertapModule]);

  const clevertapUserLogin = useCallback(
    async (user: any) => {
      const customAttributes = getKeyValue(
        user?.custom_attributes,
        'attribute_code'
      );
      const isSubscribed = customAttributes?.is_clevertap_true === '1';

      const ctEventData: any = {
        Name: `${user?.firstname} ${user?.lastname}`,
        'First Name': user?.firstname,
        'Last Name': user?.lastname,
        Identity: `+91${user?.mobilenumber}`,
        Email: user?.email,
        DOB: new Date(user?.dob),
        'Wedding Anniversary': new Date(customAttributes?.wedding_anniversary),
        Product_ids: '',
      };

      if (customAttributes?.is_clevertap_true) {
        ctEventData['MSG-whatsapp'] = isSubscribed;
        ctEventData['MSG-email'] = isSubscribed;
        ctEventData['MSG-sms'] = isSubscribed;
      }

      clevertapModule?.onUserLogin.push({ Site: ctEventData });
    },
    [clevertapModule]
  );

  const updateUserCartClevertap = ({
    cartItems,
  }: {
    cartItems: ProductItemPropTypes[] | null;
  }) => {
    const cartSkus = cartItems?.map(product => product.sku) || [];
    if (cartSkus?.length > 0) {
      const storeCode = storage.get('store_code') || DEFAULT_STORE_CODE;
      const productIds = cartSkus.map(sku => `${sku}${storeCode}`);
      if (clevertapModule) {
        clevertapModule?.setMultiValuesForKey('Product_ids', productIds);
      }
    }
  };

  const updateClevertapUserAttributes = useCallback(
    ({
      attributesToUpdate,
    }: {
      attributesToUpdate: { key: string; value: string }[];
    }) => {
      const payload: { Site: { [key: string]: string } } = { Site: {} };
      attributesToUpdate.forEach(({ key, value }) => {
        payload.Site[key] = value;
      });
      clevertapModule?.profile?.push(payload);
    },
    [clevertapModule]
  );

  const pushEvent = async ({ event, data }: EventsType) => {
    const store_code = storage.get('store_code');
    const utmParams = syncAndGetUtmParams();
    const dataToSend: any = {
      ...data,
      utm_source: utmParams.utm_source,
      utm_medium: utmParams.utm_medium,
      utm_campaign: utmParams.utm_campaign,
    };
    if (store_code && !data?.['Store Code']) {
      dataToSend['Store Code'] = store_code;
    }
    clevertapModule?.event?.push(event, dataToSend);
  };

  const pushPageEvent = useCallback(async () => {
    const paths = pathname.split('/');
    const category =
      paths[paths.length - 2] === '' ? 'home' : paths[paths.length - 2];
    const name =
      window.document.title !== '' ? window.document.title : category;
    const store_code = storage.get('store_code');
    const utmParams = syncAndGetUtmParams();
    const dataToSend: any = {
      'Page URL': `${window.location.origin}${pathname}`,
      'Page Name': name,
      'Page Category': category,
      Timestamp: new Date().getTime(),
      'Referral Source': document.referrer || 'Direct',
      Browser: navigator.userAgent,
      'Operating System': navigator?.platform || 'Unknown',
      utm_source: utmParams.utm_source,
      utm_medium: utmParams.utm_medium,
      utm_campaign: utmParams.utm_campaign,
    };
    if (store_code) {
      dataToSend['Store Code'] = store_code;
    }
    clevertapModule?.event.push('Page Viewed', dataToSend);
  }, [clevertapModule, pathname]);

  const value = useMemo(
    () => ({
      clevertapUserLogin,
      pushEvent,
      updateUserCartClevertap,
      updateClevertapUserAttributes,
    }),
    [
      clevertapUserLogin,
      pushEvent,
      updateUserCartClevertap,
      updateClevertapUserAttributes,
    ]
  );

  useEffect(() => {
    clevertapInit();
  }, [clevertapInit]);

  useEffect(() => {
    pushPageEvent();
  }, [pathname, pushPageEvent]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useCleverTap = (): useCleverTapPropTypes => {
  const context = useContext(Context);
  if (!context) {
    throw new Error('useCleverTap must be used within a CleverTapProvider');
  }
  return context;
};

export default CleverTapProvider;
